import { useEffect, useRef, useState, useContext } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  alpha,
  List,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { styled } from '@mui/material/styles';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ChangePassword from 'src/components/changePassword';
import UserContext from 'src/context/UserContext';
import { ManagementServices } from 'src/services/ManagementServices';
import { brand } from 'src/theme/brand';
import { useCustomerShopBranding } from 'src/hooks/useCustomerShopBranding';

enum IsFirstTimeUser {
  YES = 'YES',
  NO = 'NO'
}

const UserBoxButton = styled(Button)(
  ({ theme }) => `
        padding-left: ${theme.spacing(0.625)};
        padding-right: ${theme.spacing(1.25)};
        border-radius: 12px;
        background: ${brand.surface};
        border: none;
        min-height: 40px;

        &:hover {
          background: ${brand.subtle};
        }
`
);

const MenuUserBox = styled(Box)(
  ({ theme }) => `
        background: ${brand.canvas};
        padding: ${theme.spacing(2)};
`
);

const UserBoxText = styled(Box)(
  ({ theme }) => `
        text-align: left;
        padding-left: ${theme.spacing(1)};
        min-width: 0;
`
);

const UserBoxLabel = styled(Typography)(
  () => `
        font-weight: 600;
        color: ${brand.ink};
        display: block;
        font-size: 13px;
        line-height: 1.25;
        letter-spacing: -0.01em;
`
);

const UserBoxDescription = styled(Typography)(
  () => `
        color: ${brand.muted};
        font-size: 11px;
        font-weight: 500;
        line-height: 1.25;
        text-transform: capitalize;
`
);

function HeaderUserbox() {
  const [openChangePassword, setOpenChangePassword] = useState<boolean>(false);
  const [user, setUser] = useContext(UserContext);
  const { isCustomer, shopLogoUrl, shopInitial } = useCustomerShopBranding();
  const [isOpen, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (
      user?.role == 'INVENTORY_ADMIN' &&
      user?.isFirstTime == IsFirstTimeUser.YES
    ) {
      handleChangePaswordModal();
      updateStatus(user?._id, IsFirstTimeUser.NO);
    }
  }, [user]);

  const updateStatus = (userId: any, isFirstTime) => {
    const data = {
      id: userId,
      isFirstTime: isFirstTime
    };
    ManagementServices.updateInventoryUser(data);
  };

  const ref = useRef<any>(null);

  const handleOpen = (): void => {
    setOpen(true);
  };

  const handleClose = (): void => {
    setOpen(false);
  };

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/sign-in';
  };

  const handleChangePaswordModal = () => {
    setOpenChangePassword(!openChangePassword);
    handleClose();
  };

  const roleLabel = (user?.role || '')
    .toLowerCase()
    .replace(/_/g, ' ');

  return (
    <>
      <UserBoxButton color="secondary" ref={ref} onClick={handleOpen}>
        <Avatar
          alt={user?.name}
          src={isCustomer ? shopLogoUrl : ''}
          sx={{
            width: 30,
            height: 30,
            backgroundColor: brand.ink,
            fontWeight: 700,
            fontSize: 13
          }}
        >
          {isCustomer && shopLogoUrl
            ? null
            : isCustomer
            ? shopInitial
            : user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user?.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {roleLabel}
            </UserBoxDescription>
          </UserBoxText>
        </Hidden>
        <Hidden smDown>
          <KeyboardArrowDownRoundedIcon
            sx={{ ml: 0.5, color: brand.muted, fontSize: 18 }}
          />
        </Hidden>
      </UserBoxButton>
      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: '12px',
            overflow: 'hidden',
            border: `1px solid ${brand.border}`,
            boxShadow: '0 12px 32px rgba(15, 23, 42, 0.1)',
            minWidth: 220
          }
        }}
      >
        <MenuUserBox display="flex" alignItems="center">
          <Avatar
            variant="rounded"
            alt={user?.name}
            src={''}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              backgroundColor: brand.green,
              fontWeight: 700,
              fontSize: 14
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase()}
          </Avatar>
          <UserBoxText>
            <UserBoxLabel variant="body1">{user?.name}</UserBoxLabel>
            <UserBoxDescription variant="body2">
              {roleLabel}
            </UserBoxDescription>
          </UserBoxText>
        </MenuUserBox>
        <Divider sx={{ borderColor: brand.border }} />
        <List sx={{ p: 1 }} component="nav">
          <Button
            onClick={handleChangePaswordModal}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              color: brand.ink,
              gap: 1,
              fontWeight: 500
            }}
          >
            <LockOutlinedIcon fontSize="small" sx={{ color: brand.muted }} />
            <ListItemText
              primary="Change password"
              primaryTypographyProps={{ fontSize: 13.5, fontWeight: 500 }}
            />
          </Button>
        </List>
        <Divider sx={{ borderColor: brand.border }} />
        <Box sx={{ p: 1 }}>
          <Button
            onClick={logout}
            fullWidth
            sx={{
              justifyContent: 'flex-start',
              px: 1.5,
              py: 1,
              borderRadius: '8px',
              textTransform: 'none',
              color: brand.error,
              gap: 1,
              fontWeight: 500,
              '&:hover': {
                background: alpha(brand.error, 0.06)
              }
            }}
          >
            <LogoutRoundedIcon fontSize="small" />
            Sign out
          </Button>
        </Box>
      </Popover>

      <ChangePassword
        open={openChangePassword}
        onClose={handleChangePaswordModal}
      />
    </>
  );
}

export default HeaderUserbox;
